/**
 * The relay core (M3 plan R10, spec §10): a disc across a relay's aperture with a TSL swirl. Bright blue-white
 * when the relay is online; near-black with drifting dust when offline; during a reboot it flickers, lit more often
 * as the progress rises, until it holds bright. R13 attaches `mesh` at the model's `DOCK_TRANSIT` socket (the disc
 * lies in the local XY plane, like the ring; local −Z is the transit direction).
 *
 * The lane-ring variant is the same effect built from `laneCore` in reachFx.json: smaller (the ring's aperture),
 * in the lane colour, and dark when the ring is down (state 0) and lit when up (state 1).
 *
 * `setState` and `update` run per frame and allocate nothing (D-014).
 */
import { CircleGeometry, DoubleSide, Mesh, MeshBasicNodeMaterial, type Vector3 } from 'three/webgpu'
import { atan, float, floor, fract, hash, length, mix, sin, smoothstep, step, uniform, uv, vec2 } from 'three/tsl'
import { linearRgb, setLinearRgb } from './color'
import type { CoreConfig } from './config'

/** Relay: 0 offline, 1 online, 2 reboot. Lane ring: 0 down, 1 up (`ActiveStructure.state`). */
export type CoreState = 0 | 1 | 2

/** The shader inputs for a core state (linear colour, HDR intensity, dust and flicker amounts). */
export interface CoreParams {
  r: number
  g: number
  b: number
  intensity: number
  /** 0..1: how much offline dust shows. */
  dust: number
  /** 0..1: flicker depth. */
  flicker: number
  /** 0..1: the share of flicker steps that are lit. */
  bias: number
}

/** The state-driven uniforms (read access for tests and debug views). */
export interface CoreUniforms {
  readonly color: { readonly value: Vector3 }
  readonly intensity: { readonly value: number }
  readonly dust: { readonly value: number }
  readonly flicker: { readonly value: number }
  readonly bias: { readonly value: number }
  readonly time: { readonly value: number }
}

export interface RelayCore {
  readonly mesh: Mesh
  readonly uniforms: CoreUniforms
  /** The params the last `setState` wrote to the uniforms. */
  readonly params: Readonly<CoreParams>
  setState(state: CoreState, progress01: number): void
  update(dt: number): void
  /** Re-reads the config (dev-panel tuning) and re-applies the current state. */
  refresh(): void
  dispose(): void
}

/** The time uniform wraps here (s) so float32 keeps its precision. */
const TIME_WRAP_S = 3600

/** A core's online and offline colours in linear RGB, parsed once (hex parsing allocates). */
export interface CoreColors {
  readonly on: Vector3
  readonly off: Vector3
}

/** Parses the config's colours (build and tuning time only). */
export function coreColors(cfg: CoreConfig, out?: CoreColors): CoreColors {
  if (!out) return { on: linearRgb(cfg.onlineColor), off: linearRgb(cfg.offlineColor) }
  setLinearRgb(out.on, cfg.onlineColor)
  setLinearRgb(out.off, cfg.offlineColor)
  return out
}

export function createCoreParams(): CoreParams {
  return { r: 0, g: 0, b: 0, intensity: 0, dust: 0, flicker: 0, bias: 1 }
}

/**
 * Writes the uniform values for `state` into `out`. Online: the online colour and intensity. Offline (or a lane
 * ring that is down): the offline colour and intensity with full dust. Reboot: the online look, flickering at
 * `rebootFlicker` depth with a lit share equal to the progress, and dust fading as it rises. Allocation-free.
 */
export function coreParams(state: CoreState, progress01: number, cfg: CoreConfig, colors: CoreColors, out: CoreParams): CoreParams {
  const p = progress01 > 0 ? (progress01 < 1 ? progress01 : 1) : 0
  const on = colors.on
  const off = colors.off
  if (state === 1) {
    out.r = on.x
    out.g = on.y
    out.b = on.z
    out.intensity = cfg.onlineIntensity
    out.dust = 0
    out.flicker = 0
    out.bias = 1
  } else if (state === 2) {
    out.r = on.x
    out.g = on.y
    out.b = on.z
    out.intensity = cfg.onlineIntensity
    out.dust = 1 - p
    out.flicker = cfg.rebootFlicker
    out.bias = p
  } else {
    out.r = off.x
    out.g = off.y
    out.b = off.z
    out.intensity = cfg.offlineIntensity
    out.dust = 1
    out.flicker = 0
    out.bias = 1
  }
  return out
}

/** A relay core (`cfg` = `relayCore`) or a lane-ring core (`cfg` = `laneCore`) across an aperture of `apertureM`. */
export function createRelayCore(apertureM: number, cfg: CoreConfig): RelayCore {
  const params = createCoreParams()
  const colors = coreColors(cfg)
  const uTime = uniform(0)
  const uColor = uniform(linearRgb(cfg.offlineColor))
  const uIntensity = uniform(cfg.offlineIntensity)
  const uDust = uniform(1)
  const uFlicker = uniform(0)
  const uBias = uniform(1)
  const uArms = uniform(cfg.swirlArms)
  const uTwist = uniform(cfg.swirlTwist)
  const uSpeed = uniform(cfg.swirlSpeed)
  const uRimFade = uniform(cfg.rimFade)
  const uDustScale = uniform(cfg.dustScale)
  const uFlickerHz = uniform(cfg.flickerHz)

  const geometry = new CircleGeometry(apertureM, 64)
  const p = uv().sub(0.5).mul(2)
  const r = length(p)
  const angle = atan(p.y, p.x)
  const swirl = sin(angle.mul(uArms).add(r.mul(uTwist)).sub(uTime.mul(uSpeed).mul(6.2831853))).mul(0.5).add(0.5)
  const hot = r.oneMinus().max(0).pow(3)
  const glow = mix(float(0.35), float(1), swirl).mul(r.mul(0.5).oneMinus()).add(hot)
  // Flicker: a hashed step per 1/flickerHz; lit with probability `bias`.
  const lit = step(hash(floor(uTime.mul(uFlickerHz)).add(17)), uBias)
  const flick = float(1).sub(uFlicker.mul(lit.oneMinus()))
  // Offline dust: sparse grey grains drifting with the swirl.
  const grainUv = p.add(vec2(uTime.mul(0.01), uTime.mul(0.007))).mul(uDustScale).add(1000)
  const grain = step(0.92, hash(floor(grainUv.x).add(floor(grainUv.y).mul(4099)))).mul(fract(grainUv.x).sub(0.5).abs().oneMinus())
  const dust = grain.mul(uDust).mul(0.12)

  const material = new MeshBasicNodeMaterial({ side: DoubleSide, transparent: true, depthWrite: false })
  material.name = 'reachFx:relayCore'
  material.colorNode = uColor.mul(uIntensity).mul(glow).mul(flick).add(dust)
  material.opacityNode = smoothstep(float(1).sub(uRimFade), float(1), r).oneMinus()

  const mesh = new Mesh(geometry, material)
  mesh.name = 'reachFx:relayCore'

  let state: CoreState = 0
  let progress = 0

  function write(): void {
    coreParams(state, progress, cfg, colors, params)
    uColor.value.set(params.r, params.g, params.b)
    uIntensity.value = params.intensity
    uDust.value = params.dust
    uFlicker.value = params.flicker
    uBias.value = params.bias
  }
  write()

  const uniforms: CoreUniforms = { color: uColor, intensity: uIntensity, dust: uDust, flicker: uFlicker, bias: uBias, time: uTime }

  return {
    mesh,
    uniforms,
    params,
    setState(next: CoreState, progress01: number): void {
      state = next
      progress = progress01
      write()
    },
    update(dt: number): void {
      const t = uTime.value + (dt > 0 ? dt : 0)
      uTime.value = t < TIME_WRAP_S ? t : t - TIME_WRAP_S
    },
    refresh(): void {
      uArms.value = cfg.swirlArms
      uTwist.value = cfg.swirlTwist
      uSpeed.value = cfg.swirlSpeed
      uRimFade.value = cfg.rimFade
      uDustScale.value = cfg.dustScale
      uFlickerHz.value = cfg.flickerHz
      coreColors(cfg, colors)
      write()
    },
    dispose(): void {
      mesh.removeFromParent()
      geometry.dispose()
      material.dispose()
    },
  }
}
